import { ReactNode, useLayoutEffect, useRef } from 'react';
import { useAnnouncements } from './AnnouncementsContext';
import AnnouncementBubble from './AnnouncementBubble';
import styles from './AnnouncementTour.module.scss';

interface AnnouncementAnchorProps {
  /**
   * The name a bubble step points at. By default a step points at its message's feature flag key,
   * so naming the anchor after the flag is all a single-step bubble needs.
   */
  name: string;
  children: ReactNode;
}

/**
 * Marks the element inside it as a place a feature announcement's bubble may point at (HAT-1489),
 * and draws that bubble when it is the one on screen.
 *
 * The wrapper is `display: contents`, so it adds nothing to the layout: flex gaps, grid cells and
 * table rows lay out exactly as they did. The element that gets pointed at is the first one inside.
 *
 * The bubble is drawn here, in the anchor's own React tree, rather than at the top of the app: an
 * anchor inside a dialog then has its bubble inside that dialog, so pressing «הבא» is not a click
 * outside the dialog, and does not close it.
 *
 * Registering costs nothing when there is no announcement - the tour looks anchors up only for
 * unread announcements that have steps - so mark the new element in the same PR that adds it, and
 * the announcement seed can point at it by name.
 */
const AnnouncementAnchor = ({ name, children }: AnnouncementAnchorProps) => {
  const wrapper = useRef<HTMLSpanElement>(null);
  const announcements = useAnnouncements();
  const registerAnchor = announcements?.registerAnchor;
  const isOn = announcements?.isOn ?? false;
  const bubble = announcements?.bubble ?? null;

  // Every render, because what is inside can change (a button shown only for some funds). The
  // registry is a ref, so this re-renders nothing.
  useLayoutEffect(() => {
    // An empty name marks nothing: it lets one place in a list be the anchor without wrapping it apart.
    if (!isOn || !registerAnchor || !name) return undefined;
    const element = wrapper.current?.firstElementChild;
    if (!(element instanceof HTMLElement)) return undefined;
    return registerAnchor(name, element);
  });

  const isMine = !!bubble && !!wrapper.current && bubble.element === wrapper.current.firstElementChild;

  return (
    <span ref={wrapper} className={styles.anchor} data-announcement-anchor={name}>
      {children}
      {isMine && <AnnouncementBubble bubble={bubble} />}
    </span>
  );
};

export default AnnouncementAnchor;
